import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Chip, alpha } from '@mui/material'
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded'
import LocationOffRoundedIcon from '@mui/icons-material/LocationOffRounded'

import { api } from '../../../lib/api'
import { useOrgSettings } from '../../../hooks/useOrgSettings'

interface Props { siteId?: string | null; siteName?: string | null }

/// Header chip on the WO detail drawer: shows live distance from the
/// admin to the WO's site so they can sanity-check the geofence at a
/// glance. Web doesn't enforce the gate (most desktops have no GPS, and
/// the field tech uses the mobile app anyway) — this is informational.
///
/// Renders nothing when:
///   - the org has geofencing turned off
///   - the WO has no site
///   - the site has no lat/lng yet
///   - the browser denies geolocation or the device has no GPS
export function GeofenceDistanceChip({ siteId, siteName }: Props) {
  const { prefs } = useOrgSettings()
  const enabled = prefs.wo_geofence_enabled
  const radius = prefs.wo_geofence_radius_m

  // Only hit the sites endpoint when the gate is actually on and the WO
  // has a site — otherwise this chip would silently fetch every WO open
  // even on orgs that don't use the feature.
  const siteQ = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => api.sites.get(siteId!),
    enabled: !!siteId && enabled,
    staleTime: 5 * 60 * 1000,
  })

  const [pos, setPos] = useState<{ lat: number; lng: number } | null>(null)
  const [denied, setDenied] = useState(false)

  useEffect(() => {
    if (!enabled || !siteId) return
    if (!navigator.geolocation) { setDenied(true); return }
    navigator.geolocation.getCurrentPosition(
      (p) => setPos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => setDenied(true),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
    )
  }, [enabled, siteId])

  if (!enabled || !siteId) return null
  const site = siteQ.data
  if (!site || site.latitude == null || site.longitude == null) return null
  if (denied || !pos) return null

  const distanceM = haversineMetres(pos.lat, pos.lng, site.latitude, site.longitude)
  const inRange = distanceM <= radius
  const color = inRange ? '#059669' : '#D97706'
  const Icon = inRange ? LocationOnRoundedIcon : LocationOffRoundedIcon

  return (
    <Chip
      size="small"
      icon={<Icon sx={{ fontSize: 14, color: `${color} !important` }} />}
      label={`${formatDistance(distanceM)} from ${siteName ?? '—'}`}
      sx={{
        height: 24,
        fontSize: 11.5,
        fontWeight: 700,
        color,
        bgcolor: alpha(color, 0.10),
        border: '1px solid',
        borderColor: alpha(color, 0.25),
        '& .MuiChip-icon': { ml: '6px !important' },
      }}
    />
  )
}

/// Standard great-circle distance. Earth radius = 6371000 m. Accurate
/// enough at the scale we care about (tens of metres to a few km).
function haversineMetres(lat1: number, lng1: number, lat2: number, lng2: number) {
  const R = 6371000
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}

function formatDistance(m: number) {
  if (m < 1000) return `${Math.round(m)} m`
  return `${(m / 1000).toFixed(1)} km`
}
